import React from "react";
import { useTranslation } from "react-i18next";
import { useCookies } from "react-cookie";
import styled from "styled-components";

import { Button, Container, Spinner, Typography } from "components/ui";
import { ACCESS_TOKEN_COOKIE } from "common/constants/cookies";

import { STAFF_FLAG_KEY, isStaffFlagSet } from "./staffFlag";

export type AppErrorVariant = "unavailable" | "notFound" | "unexpected";

interface Props {
	variant: AppErrorVariant;
	/** Which provider or request failed. Shown to staff, logged for everyone. */
	source?: string;
	/** HTTP status, when there was a response at all. */
	status?: number;
	/** Subdomain that resolved to nothing - only meaningful for `notFound`. */
	subdomain?: string;
	onRetry?: () => void;
	/** Server's Retry-After, in ms. The schedule backs off from this. */
	retryAfterMs?: number;
}

/** Where the backoff starts when the server didn't send a Retry-After. */
const DEFAULT_RETRY_MS = 15000;
/** Waiting longer than this reads as "given up", however honest the countdown. */
const MAX_RETRY_MS = 60000;

const nextDelay = (current: number) => Math.min(current * 2, MAX_RETRY_MS);

/**
 * Illustrations we already ship, matched to what each failure actually means.
 * "Be right back" is the outage one - it says temporary, which is the single
 * most useful thing this page can communicate.
 */
const ILLUSTRATION: Record<AppErrorVariant, string> = {
	unavailable: "/images/HegiasIlluBeRightBack.png",
	notFound: "/images/HegiasIllu404.png",
	unexpected: "/images/HegiasIllu500.png",
};

/**
 * Full-page failure state for the four providers that gate the app's boot.
 *
 * Built from the ui component library rather than hand-rolled markup so it
 * matches views/Error/ErrorPage: same grey ground, same lime heading, same
 * buttons, same illustration treatment. A user who meets both should not be
 * able to tell they were built two years apart.
 */
const AppError: React.FC<Props> = ({
	variant,
	source,
	status,
	subdomain,
	onRetry,
	retryAfterMs,
}) => {
	const { t } = useTranslation();
	const [cookies] = useCookies([ACCESS_TOKEN_COOKIE]);

	// Only an unreachable backend is worth retrying on a timer. A 404 means the
	// address is wrong and a 500 means the request itself is broken - repeating
	// either just adds load to a system already having a bad day.
	const canAutoRetry = variant === "unavailable" && !!onRetry;

	const [delay, setDelay] = React.useState(retryAfterMs || DEFAULT_RETRY_MS);
	const [secondsLeft, setSecondsLeft] = React.useState(() =>
		Math.ceil((retryAfterMs || DEFAULT_RETRY_MS) / 1000),
	);
	const [retrying, setRetrying] = React.useState(false);

	const [detailsOpen, setDetailsOpen] = React.useState(false);
	const [details, setDetails] = React.useState<unknown>(null);
	const [detailsState, setDetailsState] = React.useState<
		"idle" | "loading" | "failed"
	>("idle");

	const timestamp = React.useMemo(() => new Date().toISOString(), []);

	// PageTitles lives inside the children a failed BootGate replaces, so it
	// never mounts to set a title here - the tab would otherwise keep whatever
	// name the previous page left behind. On recovery PageTitles mounts again
	// and takes the title back.
	React.useEffect(() => {
		document.title = String(t(`app.error.${variant}.pageTitle`));
	}, [variant, t]);

	const runRetry = React.useCallback(() => {
		if (!onRetry) return;

		setRetrying(true);
		onRetry();

		// The gate above re-renders on its own once the request settles; this
		// only clears the button's busy state if it doesn't.
		window.setTimeout(() => setRetrying(false), 2000);
	}, [onRetry]);

	// Countdown, then retry, then wait twice as long before trying again.
	React.useEffect(() => {
		if (!canAutoRetry) return undefined;

		const tick = window.setInterval(() => {
			setSecondsLeft((remaining) => {
				if (remaining > 1) return remaining - 1;

				runRetry();
				setDelay((current) => {
					const grown = nextDelay(current);
					setSecondsLeft(Math.ceil(grown / 1000));
					return grown;
				});

				return 0;
			});
		}, 1000);

		return () => window.clearInterval(tick);
	}, [canAutoRetry, runRetry, delay]);

	const loadDetails = async () => {
		if (detailsOpen) {
			setDetailsOpen(false);
			return;
		}

		setDetailsOpen(true);

		if (details || detailsState === "loading") return;

		setDetailsState("loading");

		try {
			const response = await fetch(
				`${process.env.REACT_APP_BASE_URL}/health/detailed`,
				{
					headers: {
						Accept: "application/json",
						Authorization: `Bearer ${cookies[ACCESS_TOKEN_COOKIE]}`,
					},
				},
			);

			// 403 here is the expected answer for anyone who set the flag by
			// hand, and for staff whose token predates the hegiasStaff claim.
			// Either way there is nothing more to show.
			if (!response.ok) {
				setDetailsState("failed");
				return;
			}

			setDetails(await response.json());
			setDetailsState("idle");
		} catch {
			setDetailsState("failed");
		}
	};

	// Only the tenant gate knows a subdomain; a 404 from anywhere else has no
	// address to name, so it gets the generic wording rather than a sentence
	// with an unresolved placeholder in it.
	const bodyKey =
		variant === "notFound" && !subdomain
			? "app.error.notFound.bodyGeneric"
			: `app.error.${variant}.body`;

	return (
		<Page>
			<Typography variant="h1" color="primary">
				{String(t(`app.error.${variant}.title`))}
			</Typography>

			<Description>
				<Typography variant="p">
					{String(t(bodyKey, subdomain ? { subdomain } : undefined))}
				</Typography>
			</Description>

			{canAutoRetry && (
				<Typography variant="p" color="grey500" aria-live="polite">
					{retrying
						? String(t("app.error.retrying"))
						: String(
								t("app.error.retryingIn", {
									seconds: secondsLeft,
								}),
						  )}
				</Typography>
			)}

			{onRetry && (
				<Actions>
					<Button onClick={runRetry} loading={retrying}>
						{String(t("app.error.tryAgain"))}
					</Button>
				</Actions>
			)}

			{/* Decorative: the heading and body already carry the whole message. */}
			<Illustration
				src={ILLUSTRATION[variant]}
				alt=""
				aria-hidden="true"
			/>

			<Footer>
				<Timestamp>{timestamp}</Timestamp>

				{isStaffFlagSet() && (
					<DetailsToggle type="button" onClick={loadDetails}>
						{String(
							t(
								detailsOpen
									? "app.error.hideDetails"
									: "app.error.showDetails",
							),
						)}
					</DetailsToggle>
				)}

				{detailsOpen && (
					<Details>
						<DetailRow>
							<DetailLabel>source</DetailLabel>
							<DetailValue>{source || "unknown"}</DetailValue>
						</DetailRow>
						<DetailRow>
							<DetailLabel>status</DetailLabel>
							<DetailValue>{status ?? "no response"}</DetailValue>
						</DetailRow>

						{detailsState === "loading" && <Spinner />}

						{detailsState === "failed" && (
							<DetailValue>
								{String(t("app.error.detailsUnavailable"))}
							</DetailValue>
						)}

						{details != null && (
							<DetailPre>
								{JSON.stringify(details, null, 2)}
							</DetailPre>
						)}
					</Details>
				)}
			</Footer>
		</Page>
	);
};

/**
 * Records whether this browser belongs to a HEGIAS account, so the Details
 * control can be drawn before the app has a backend to ask.
 *
 * This decides what is rendered and nothing else: the server still authorises
 * every /health/detailed call against a signed token claim, so setting the flag
 * by hand buys a button that answers 403.
 */
export const setStaffFlag = (isStaff: boolean) => {
	try {
		if (isStaff) {
			window.localStorage.setItem(STAFF_FLAG_KEY, "true");
		} else {
			window.localStorage.removeItem(STAFF_FLAG_KEY);
		}
	} catch {
		// Private browsing and blocked storage: the button just won't appear.
	}
};

const Page = styled(Container)`
	width: 100%;
	min-height: 100vh;
	background-color: var(--color-bg-workspace);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	text-align: center;
	padding: var(--page-padding);
`;

const Description = styled.div`
	max-width: 52ch;
`;

const Actions = styled.div`
	display: flex;
	gap: 12px;
	justify-content: center;
	margin-top: 4px;
`;

const Illustration = styled.img`
	height: auto;
	width: 80%;
	max-width: 420px;
	margin-top: 8px;
`;

const Footer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	width: 100%;
	max-width: 640px;
`;

const Timestamp = styled.span`
	font-family: var(--font-family);
	font-size: var(--font-size-micro);
	color: var(--color-text-disabled);
`;

const DetailsToggle = styled.button`
	background: none;
	border: none;
	color: var(--color-secondary);
	font-family: var(--font-family);
	font-size: var(--font-size-caption);
	cursor: pointer;
	text-decoration: underline;
	padding: 0;

	&:focus-visible {
		outline: var(--focus-ring);
		outline-offset: var(--focus-ring-offset);
	}
`;

const Details = styled.div`
	width: 100%;
	text-align: left;
	background-color: var(--color-surface);
	border: var(--border-default);
	border-radius: var(--radius-md);
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const DetailRow = styled.div`
	display: flex;
	gap: 12px;
	font-family: var(--font-family);
	font-size: var(--font-size-caption);
`;

const DetailLabel = styled.span`
	color: var(--color-text-secondary);
	min-width: 64px;
`;

const DetailValue = styled.span`
	color: var(--color-text);
	word-break: break-word;
`;

const DetailPre = styled.pre`
	font-size: var(--font-size-micro);
	color: var(--color-text-secondary);
	background-color: var(--color-bg-workspace);
	border-radius: var(--radius-sm);
	padding: 12px;
	margin: 0;
	overflow-x: auto;
	white-space: pre-wrap;
	word-wrap: break-word;
`;

export default AppError;
